import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { api, fehlertext } from "./api";
import { Fehler, Karte, Knopf, Laedt, Seitenkopf, Textknopf } from "./ui";

/**
 * Die internen Notizen aus den Berichten, gesammelt.
 *
 * Der Mitarbeiter schreibt am Objekt einen Satz in den internen Kasten seines
 * Berichts. Ohne diese Seite laege er in einem von zweihundert Berichten und
 * waere damit nicht geschrieben worden.
 *
 * Zwei Entscheidungen, die man spaeter nachlesen will:
 *
 * **Abhaken statt loeschen.** Eine erledigte Notiz verschwindet aus der
 * offenen Liste und bleibt lesbar. Wer sie abgehakt hat, steht dabei. Ohne den
 * Haken waere die Seite eine Liste, die nur waechst, und nach drei Wochen
 * faellt die eine wichtige Zeile darin nicht mehr auf.
 *
 * **Nur die Geschaeftsleitung.** Der Verfasser sieht seine eigene Notiz
 * weiterhin an seinem Bericht. Die Sammlung ueber alle Kunden hinweg ist
 * etwas anderes als die eigene Notiz.
 */

interface Notiz {
  id: string;
  number: number;
  status: string;
  notiz: string;
  geschrieben_am: string | null;
  erledigt_am: string | null;
  finished_at: string | null;
  objekt: string;
  kunde: string;
  verfasser: string | null;
  abgehakt_von: string | null;
}

export function NotizenSeite() {
  const { t, i18n } = useTranslation();
  const [zeigeErledigte, setZeigeErledigte] = useState(false);
  const [notizen, setNotizen] = useState<Notiz[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = useCallback(async () => {
    setNotizen(null);
    const a = await api.get<{ notizen: Notiz[] }>(
      `/berichte/notizen${zeigeErledigte ? "?erledigt=ja" : ""}`
    );
    setNotizen(a.notizen);
  }, [zeigeErledigte]);

  useEffect(() => {
    void laden().catch((e) => setFehler(fehlertext(e, t("fehler.laden"))));
  }, [laden, t]);

  async function abhaken(id: string, erledigt: boolean) {
    setFehler(null);
    /*
     * Sofort aus der Liste nehmen und danach den Server fragen. Wer zehn
     * Notizen abarbeitet, soll nicht zehnmal auf das Neuladen warten; geht es
     * schief, holt das Neuladen im finally den Stand des Servers zurueck.
     */
    setNotizen((alt) => (alt ?? []).filter((n) => n.id !== id));
    try {
      await api.post(`/berichte/notizen/${id}/erledigt`, { erledigt });
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    } finally {
      await laden().catch(() => undefined);
    }
  }

  function datum(wert: string | null): string {
    if (!wert) return "";
    return new Date(wert).toLocaleDateString(i18n.language, {
      day: "2-digit", month: "2-digit", year: "numeric"
    });
  }

  return (
    <>
      <Seitenkopf
        titel={t("notiz.reiter")}
        hinweis={t("notiz.reiterHinweis")}
        aktionen={
          <Knopf art="neben" onClick={() => setZeigeErledigte((x) => !x)}>
            {zeigeErledigte ? t("notiz.zeigeOffene") : t("notiz.zeigeErledigte")}
          </Knopf>
        }
      />
      <Fehler text={fehler} />

      {!notizen ? (
        <Laedt />
      ) : notizen.length === 0 ? (
        <Karte>
          <p className="text-sm text-leise">
            {zeigeErledigte ? t("notiz.keineErledigten") : t("notiz.keineOffenen")}
          </p>
        </Karte>
      ) : (
        <ul className="space-y-3">
          {notizen.map((n) => (
            <li key={n.id}>
              <Karte className={zeigeErledigte ? "opacity-70" : "border-s-4 border-red-700"}>
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                  <span className="font-bold">{n.kunde}</span>
                  <span className="text-sm text-leise">{n.objekt}</span>
                  <span className="ms-auto text-sm text-leise tabular-nums">
                    {datum(n.geschrieben_am)}
                  </span>
                </div>
                <p className="mt-2 whitespace-pre-line text-sm">{n.notiz}</p>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                  <span className="text-xs text-leise">
                    {t("notiz.von", {
                      name: n.verfasser ?? "",
                      nummer: n.number
                    })}
                  </span>
                  <Link
                    to={`/pruefungen/${n.id}`}
                    className="text-xs font-semibold text-verweis underline"
                  >
                    {t("notiz.zumBericht")}
                  </Link>
                  <span className="ms-auto">
                    {zeigeErledigte ? (
                      <Textknopf onClick={() => void abhaken(n.id, false)}>
                        {t("notiz.wiederOeffnen")}
                      </Textknopf>
                    ) : (
                      <Knopf art="neben" onClick={() => void abhaken(n.id, true)}>
                        {t("notiz.erledigt")}
                      </Knopf>
                    )}
                  </span>
                </div>
                {zeigeErledigte && n.abgehakt_von && (
                  <p className="mt-2 text-xs text-leise">
                    {t("notiz.abgehakt", {
                      name: n.abgehakt_von,
                      datum: datum(n.erledigt_am)
                    })}
                  </p>
                )}
              </Karte>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
